import { createEntityAdapter, createSlice } from "@reduxjs/toolkit";

import { getMyProfile } from "../user/actions";
import {
  getAllOrganizationDetails,
  getMeOrganizations,
  getOrganizationAssignableUsers,
  getOrganizationDetails,
  getOrganizationUsers,
  getWorkspaceDetails,
} from "./action";

export const organizationEntityAdapter = createEntityAdapter({
  selectId: (entity) => entity.organizationId,
});

const initialState = organizationEntityAdapter.getInitialState({
  filteredOrganizations: [],
  totalFilteredOrganizations: 0,
});

export const organizationSlice = createSlice({
  name: "organization",
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getMyProfile.fulfilled, (state, action) => {
      if (action.payload.entities?.organization) {
        organizationEntityAdapter.upsertMany(state, action.payload.entities.organization);
      }
    });
    builder.addCase(getOrganizationDetails.fulfilled, (state, action) => {
      if (action.payload.entities?.organization) {
        const updatedEntities = Object.keys(action.payload.entities.organization).reduce(
          (acc, key) => ({
            ...acc,
            [key]: {
              ...state.entities[key],
              ...action.payload.entities.organization[key],
            },
          }),
          state.entities,
        );

        state.entities = {
          ...action.payload.entities.organization,
          ...updatedEntities,
        };
      }
    });
    builder.addCase(getOrganizationUsers.fulfilled, (state, action) => {
      const orgId = action.meta.arg.payload.organizationId;
      if (state.entities[orgId]) {
        state.entities[orgId].orgUsers = action.payload?.users;
        state.entities[orgId].totalUsers = action.payload?.total;
      } else {
        const { users } = action.payload;
        const newOrg = {
          isDeleted: false,
          client_secret: "",
          client_id: "",
          account_id: "",
          _id: orgId || "",
          organizationId: orgId || "",
          name: "",
          displayName: "",
          logo: "",
          orgUsers: users,
          totalUsers: action.payload?.total,
          planId: "",
        };

        organizationEntityAdapter.addOne(state, newOrg);
        state.totalOrganizationWorkspaces += 1;
      }
    });
    builder.addCase(getOrganizationAssignableUsers.fulfilled, (state, action) => {
      const orgId = action.meta.arg.entityId;

      if (orgId) {
        state.entities[orgId] = state.entities[orgId] ?? {};

        if (action.meta.arg.upsert) {
          const existingUsers = state.entities[orgId].assignableUsers || [];
          const newUsers = action.payload?.users || [];

          const userMap = new Map(existingUsers.map((user) => [user.userId, user]));

          newUsers.forEach((user) => userMap.set(user.userId, user));

          state.entities[orgId].assignableUsers = Array.from(userMap.values());
        } else {
          state.entities[orgId].assignableUsers = action.payload?.users;
        }

        state.entities[orgId].totalAssignableUsers = action.payload?.total || 0;
      }
    });
    builder.addCase(getAllOrganizationDetails.fulfilled, (state, action) => {
      const orgId = action.meta.arg.entityId;

      if (orgId) {
        state.entities[orgId] = state.entities[orgId] ?? {};
        state.entities[orgId].displayName = action.payload.organization?.displayName;
        state.entities[orgId].logo = action.payload.organization?.logo;
        state.entities[orgId].planId = action.payload.organization?.planId;
      }
    });
    builder.addCase(getMeOrganizations.fulfilled, (state, action) => {
      if (action.payload.status) {
        if (action.meta.arg.payload.params.page === 1) {
          state.filteredOrganizations = action.payload.data;
        } else {
          const newOrganizations = [...state.filteredOrganizations, ...action.payload.data];
          const newOrganizationsMap = new Map(newOrganizations.map((org) => [org.organizationId, org]));

          state.filteredOrganizations = Array.from(newOrganizationsMap.values());
        }
        state.totalFilteredOrganizations = action.payload.total;
      }
    });
    builder.addCase(getWorkspaceDetails.fulfilled, (state, action) => {
      if (action.payload.organization) {
        organizationEntityAdapter.upsertOne(state, action.payload.organization);
      }
    });
  },
});

export default organizationSlice.reducer;
